/**
 * Un paso con los dos canales, donde conectar uno alcanza.
 *
 * El wizard de operador tiene Telegram y WhatsApp como pasos **separados y
 * secuenciales**. Para el alta pública se unifican, porque la regla de negocio
 * ya está del lado correcto: `onboarding.ts` acepta
 * `telegram === 'done' || whatsapp === 'done'`.
 *
 * **Telegram va primero.** Se conecta con un token de @BotFather en dos
 * minutos; WhatsApp exige cuenta de negocio, un número aparte y —hasta que
 * exista Embedded Signup (ADR-028, GKTIBO-18)— trabajo nuestro. Poner primero
 * el difícil es la forma más rápida de que alguien abandone en el paso 2 de 4.
 */

import type { RespuestaDeOnboarding } from '@chatbot/shared-types';
import type { ReactNode } from 'react';
import { TelegramSection } from '../../components/tenant-sections/TelegramSection';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { textos } from './textos';

interface PasoDeCanalesProps {
  canales: RespuestaDeOnboarding['channels'];
  tenantId: string;
  /** El borrador de canales que ya está guardado, para rellenar los formularios. */
  valor: { telegram?: unknown; whatsapp?: unknown };
  onGuardado?: (patch: unknown) => void;
}

/**
 * Un canal cuenta sólo si puede **recibir**.
 *
 * `token-only` es un bot sordo: el token está cargado pero al canal le falta el
 * `webhookSecretToken`. Darlo por conectado dejaría al dueño avanzar con un bot
 * al que nadie puede escribirle, y el síntoma sería «no responde» sin causa.
 */
function estaConectado(estado: string): boolean {
  return estado === 'done';
}

function TarjetaDeCanal({
  id,
  nombre,
  promesa,
  comoSeHace,
  conectado,
  hayOtroConectado,
  formulario,
}: {
  id: 'telegram' | 'whatsapp';
  nombre: string;
  promesa: string;
  comoSeHace: string;
  conectado: boolean;
  hayOtroConectado: boolean;
  /** El formulario real, compartido con el wizard de operador (TASK-06). */
  formulario: ReactNode;
}) {
  return (
    <Card data-testid={`tarjeta-${id}`} className="alta-canal">
      <div className="alta-canal-cabecera">
        <h3 className="card-title">{nombre}</h3>
        {conectado ? (
          <Badge color="success">{textos.canales.conectado}</Badge>
        ) : (
          // Con el otro ya conectado, éste es una POSIBILIDAD y no una deuda.
          // Un paso que se ve incompleto para siempre entrena a ignorar los
          // indicadores.
          <Badge color="neutral">
            {hayOtroConectado ? textos.canales.disponible : textos.canales.sinConectar}
          </Badge>
        )}
      </div>
      <p className="alta-canal-promesa">{promesa}</p>
      <p className="alta-canal-como">{comoSeHace}</p>
      {formulario}
    </Card>
  );
}

export function PasoDeCanales({ canales, tenantId, valor, onGuardado }: PasoDeCanalesProps) {
  const telegramListo = estaConectado(canales.telegram);
  const whatsappListo = estaConectado(canales.whatsapp);
  const completo = telegramListo || whatsappListo;

  return (
    <section className="alta-paso" data-testid="canales-completo" data-completo={String(completo)}>
      <h2 className="card-title">{textos.canales.titulo}</h2>
      {/* El alivio primero: se ven dos tarjetas y hay que decir de entrada que
          no hacen falta las dos — y menos la que requiere hablar con nosotros. */}
      <p className="alta-paso-ayuda">{textos.canales.ayuda}</p>

      <div className="alta-canales">
        <TarjetaDeCanal
          id="telegram"
          nombre={textos.canales.telegram.nombre}
          promesa={textos.canales.telegram.promesa}
          comoSeHace={textos.canales.telegram.comoSeHace}
          conectado={telegramListo}
          hayOtroConectado={whatsappListo}
          // El MISMO formulario que usa el wizard de operador. Tener dos es
          // cómo el payload del wizard se separó cinco veces (L26, regla C7):
          // el próximo campo que Meta agregue se agrega en uno solo.
          formulario={
            <TelegramSection
              mode="wizard"
              testId="alta-seccion-telegram"
              tenantId={tenantId}
              value={(valor.telegram ?? {}) as never}
              onSaved={(patch) => onGuardado?.({ telegram: patch })}
            />
          }
        />
        <TarjetaDeCanal
          id="whatsapp"
          nombre={textos.canales.whatsapp.nombre}
          promesa={textos.canales.whatsapp.promesa}
          comoSeHace={textos.canales.whatsapp.comoSeHace}
          conectado={whatsappListo}
          hayOtroConectado={telegramListo}
          // WhatsApp NO muestra formulario, a propósito.
          //
          // `WhatsAppSection` pide «Phone Number ID», «Access Token» y «App
          // Secret»: son valores de la consola de desarrollador de Meta, que un
          // dueño no tiene ni sabe qué son. Mostrárselos contradice la promesa
          // de la tarjeta —«lo conectamos nosotros»— y lo deja trabado en el
          // paso que más importa.
          //
          // Cuando exista Embedded Signup (GKTIBO-18) esto pasa a ser un botón
          // que abre el flujo de Meta, no este formulario.
          formulario={<p className="alta-canal-como">{textos.canales.whatsapp.comoPedirlo}</p>}
        />
      </div>
    </section>
  );
}
